import React from 'react';

/**
 * Props for the LoadingSpinner component
 */
interface LoadingSpinnerProps {
	/** Size of the spinner */
	size?: 'small' | 'medium' | 'large';
	/** Custom message to display */
	message?: string;
	/** Whether to show the spinner in fullscreen mode */
	fullscreen?: boolean;
	/** Custom className for styling */
	className?: string;
}

/**
 * A reusable loading spinner component with multiple size options.
 *
 * Features:
 * - Multiple size variants (small, medium, large)
 * - Customizable loading message
 * - Fullscreen mode for page-level loading
 * - Smooth animations and accessible design
 * - Consistent with the application's design system
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LoadingSpinner />
 *
 * // Custom size and message
 * <LoadingSpinner size="large" message="Loading project..." />
 *
 * // Fullscreen loading
 * <LoadingSpinner fullscreen message="Loading page..." />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
	size = 'medium',
	message = 'Loading...',
	fullscreen = false,
	className = '',
}) => {
	// Size configurations
	const sizeConfig = {
		small: {
			spinner: 'w-4 h-4',
			container: 'p-2',
			text: 'text-xs',
		},
		medium: {
			spinner: 'w-8 h-8',
			container: 'p-4',
			text: 'text-sm',
		},
		large: {
			spinner: 'w-12 h-12',
			container: 'p-6',
			text: 'text-base',
		},
	};

	const config = sizeConfig[size];

	const spinnerContent = (
		<div
			className={`flex flex-col items-center justify-center ${config.container} ${className}`}>
			{/* Animated spinner */}
			<div
				className={`${config.spinner} border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-2`}
				role='status'
				aria-label='Loading'
			/>
			{/* Loading message */}
			{message && (
				<p className={`${config.text} text-gray-600 text-center`}>{message}</p>
			)}
		</div>
	);

	// Fullscreen mode
	if (fullscreen) {
		return (
			<div className='fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50'>
				<div className='bg-white rounded-lg shadow-lg border'>
					{spinnerContent}
				</div>
			</div>
		);
	}

	// Inline mode
	return spinnerContent;
};

/**
 * A specialized loading component for page transitions.
 *
 * Provides a consistent loading experience across route changes
 * with appropriate sizing and messaging for page-level loading.
 */
export const PageLoadingSpinner: React.FC<{ message?: string }> = ({
	message = 'Loading page...',
}) => (
	<div className='min-h-screen flex items-center justify-center bg-gray-50'>
		<LoadingSpinner size='large' message={message} />
	</div>
);

/**
 * A minimal loading component for inline content loading.
 *
 * Perfect for loading states within components or sections
 * without disrupting the overall page layout.
 */
export const InlineLoadingSpinner: React.FC<{ message?: string }> = ({
	message = 'Loading...',
}) => (
	<div className='flex items-center justify-center py-8'>
		<LoadingSpinner size='medium' message={message} />
	</div>
);
